import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send, Leaf } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/data";
import { Reveal, LeafDivider } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Vul alstublieft alle velden in.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Bedankt voor je bericht. We nemen snel contact met je op.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Er ging iets mis. Probeer het later opnieuw of mail ons direct.");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section id="contact" className="py-24 lg:py-28 bg-brand-cream">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <LeafDivider label="Contact" />
          <h2 className="font-display text-4xl sm:text-5xl text-brand-forest mt-3">Neem contact op</h2>
          <p className="font-body text-brand-inkSoft mt-4 text-lg leading-relaxed">
            Heb je een vraag of wil je een workshop of sessie plannen? Stuur ons gerust een bericht of bel ons.
            Onze mailbox wordt dagelijks gelezen.
          </p>

          <div className="mt-8 space-y-4">
            <a href={CONTACT.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 group">
              <span className="grid place-items-center w-11 h-11 rounded-full bg-brand-sage/15 text-brand-sage group-hover:bg-brand-sage group-hover:text-white transition-all"><Phone size={18} /></span>
              <span className="font-body text-brand-ink group-hover:text-brand-forest">{CONTACT.phone}</span>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
              <span className="grid place-items-center w-11 h-11 rounded-full bg-brand-sage/15 text-brand-sage group-hover:bg-brand-sage group-hover:text-white transition-all"><Mail size={18} /></span>
              <span className="font-body text-brand-ink group-hover:text-brand-forest">{CONTACT.email}</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="grid place-items-center w-11 h-11 rounded-full bg-brand-sage/15 text-brand-sage"><MapPin size={18} /></span>
              <span className="font-body text-brand-ink">{CONTACT.location}</span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <form onSubmit={submit} data-testid="contact-form" className="card-soft rounded-2xl p-7 sm:p-9">
            <div className="space-y-5">
              <div>
                <label className="eyebrow !text-brand-forest !tracking-[0.2em]">Naam</label>
                <input data-testid="contact-name-input" value={form.name} onChange={set("name")} className="mt-2 w-full rounded-lg border border-brand-gold/25 bg-brand-cream/60 px-4 py-3 font-body text-brand-ink outline-none focus:border-brand-sage" placeholder="Je naam" />
              </div>
              <div>
                <label className="eyebrow !text-brand-forest !tracking-[0.2em]">E-mail</label>
                <input data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} className="mt-2 w-full rounded-lg border border-brand-gold/25 bg-brand-cream/60 px-4 py-3 font-body text-brand-ink outline-none focus:border-brand-sage" placeholder="je@email.nl" />
              </div>
              <div>
                <label className="eyebrow !text-brand-forest !tracking-[0.2em]">Bericht</label>
                <textarea data-testid="contact-message-input" value={form.message} onChange={set("message")} rows={4} className="mt-2 w-full rounded-lg border border-brand-gold/25 bg-brand-cream/60 px-4 py-3 font-body text-brand-ink outline-none focus:border-brand-sage resize-none" placeholder="Waar kunnen we je mee helpen?" />
              </div>
              <button
                data-testid="contact-submit-button"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-forest text-brand-cream text-sm uppercase tracking-[0.15em] transition-all hover:bg-brand-forestDeep disabled:opacity-60"
              >
                {loading ? "Versturen..." : <>Verstuur bericht <Send size={15} /></>}
              </button>
              <p className="flex items-center justify-center gap-2 text-brand-inkSoft text-xs font-body"><Leaf size={12} className="text-brand-sage" /> We behandelen je gegevens vertrouwelijk.</p>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
